import { z } from 'zod';
/*
 * Formulardefinition des Formular-Builders. Dieselbe Logik (Sichtbarkeit,
 * Seitenverzweigung, Validierung) wird im Browser und auf dem Server verwendet –
 * der Server vertraut den Daten des Clients nie, sondern prüft sie neu.
 */
export const FIELD_TYPES = [
    'text',
    'textarea',
    'email',
    'phone',
    'number',
    'date',
    'select',
    'radio',
    'checkboxes',
    'checkbox',
    'file',
    'heading',
    'paragraph',
];
/** Felder ohne Eingabe (nur Gestaltung). */
export const STATIC_FIELD_TYPES = ['heading', 'paragraph'];
/** Zuordnung eines Felds zu Vorgangsdaten (Ticket, Kontakt, Gerät). */
export const FIELD_MAPPINGS = [
    'firstName',
    'lastName',
    'company',
    'email',
    'phone',
    'street',
    'postalCode',
    'city',
    'subject',
    'description',
    'deviceType',
    'manufacturer',
    'model',
    'serialNumber',
    'accessories',
];
export const MAPPING_LABELS = {
    firstName: 'Vorname',
    lastName: 'Nachname',
    company: 'Firma',
    email: 'E-Mail-Adresse',
    phone: 'Telefon',
    street: 'Straße',
    postalCode: 'PLZ',
    city: 'Ort',
    subject: 'Betreff',
    description: 'Beschreibung',
    deviceType: 'Gerätetyp',
    manufacturer: 'Hersteller',
    model: 'Modell',
    serialNumber: 'Seriennummer',
    accessories: 'Zubehör',
};
const keySchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,49}$/, 'Schlüssel: Kleinbuchstaben, Ziffern und _, beginnend mit Buchstabe');
export const conditionSchema = z.object({
    field: keySchema,
    op: z.enum(['equals', 'notEquals', 'contains', 'notContains', 'isEmpty', 'notEmpty']),
    value: z.string().max(200).optional(),
});
/** Alle (`all`) bzw. mindestens eine (`any`) Bedingung muss erfüllt sein. */
export const conditionGroupSchema = z.object({
    mode: z.enum(['all', 'any']).default('all'),
    conditions: z.array(conditionSchema).min(1).max(20),
});
export const fieldSchema = z.object({
    id: z.string().min(1).max(40),
    key: keySchema,
    type: z.enum(FIELD_TYPES),
    label: z.string().trim().min(1).max(300),
    help: z.string().max(1000).optional(),
    placeholder: z.string().max(200).optional(),
    required: z.boolean().default(false),
    options: z.array(z.object({ value: z.string().min(1).max(100), label: z.string().min(1).max(200) })).max(100).optional(),
    validation: z
        .object({
        minLength: z.number().int().min(0).max(10_000).optional(),
        maxLength: z.number().int().min(1).max(20_000).optional(),
        min: z.number().optional(),
        max: z.number().optional(),
        pattern: z.string().max(200).optional(),
        patternMessage: z.string().max(200).optional(),
        maxFiles: z.number().int().min(1).max(10).optional(),
    })
        .optional(),
    visibleIf: conditionGroupSchema.optional(),
    mapsTo: z.enum(FIELD_MAPPINGS).optional(),
    width: z.enum(['full', 'half']).default('full'),
});
export const pageSchema = z.object({
    id: z.string().min(1).max(40),
    title: z.string().trim().min(1).max(200),
    description: z.string().max(1000).optional(),
    fields: z.array(fieldSchema).max(100),
    /** Verzweigung: erste zutreffende Regel bestimmt die nächste Seite; sonst `defaultNext` bzw. die folgende Seite. */
    branches: z.array(z.object({ when: conditionGroupSchema, goTo: z.string().max(40) })).max(20).default([]),
    defaultNext: z.string().max(40).optional(),
});
export const SUBMIT = '__submit__';
export const formDefinitionSchema = z
    .object({
    pages: z.array(pageSchema).min(1).max(30),
    submitLabel: z.string().trim().min(1).max(60).default('Absenden'),
    confirmation: z
        .object({
        title: z.string().trim().min(1).max(200).default('Vielen Dank!'),
        message: z.string().max(2000).default('Ihre Anfrage ist bei uns eingegangen. Wir melden uns schnellstmöglich.'),
    })
        .default({}),
})
    .superRefine((def, ctx) => {
    const keys = new Set();
    const fieldIds = new Set();
    const pageIds = new Set(def.pages.map((p) => p.id));
    if (pageIds.size !== def.pages.length)
        ctx.addIssue({ code: 'custom', message: 'Seiten-IDs müssen eindeutig sein' });
    def.pages.forEach((page, pi) => {
        for (const target of [...page.branches.map((b) => b.goTo), page.defaultNext].filter(Boolean)) {
            if (target !== SUBMIT && !pageIds.has(target))
                ctx.addIssue({ code: 'custom', path: ['pages', pi], message: `Seite „${page.title}“ verweist auf eine unbekannte Seite` });
            const targetIndex = def.pages.findIndex((p) => p.id === target);
            if (targetIndex !== -1 && targetIndex <= pi)
                ctx.addIssue({ code: 'custom', path: ['pages', pi], message: `Seite „${page.title}“ darf nur auf spätere Seiten verzweigen` });
        }
        page.fields.forEach((f, fi) => {
            if (fieldIds.has(f.id))
                ctx.addIssue({ code: 'custom', path: ['pages', pi, 'fields', fi], message: 'Feld-IDs müssen eindeutig sein' });
            fieldIds.add(f.id);
            if (STATIC_FIELD_TYPES.includes(f.type))
                return;
            if (keys.has(f.key))
                ctx.addIssue({ code: 'custom', path: ['pages', pi, 'fields', fi], message: `Feldschlüssel „${f.key}“ ist doppelt` });
            keys.add(f.key);
            if (['select', 'radio', 'checkboxes'].includes(f.type) && !f.options?.length) {
                ctx.addIssue({ code: 'custom', path: ['pages', pi, 'fields', fi], message: `„${f.label}“ benötigt Auswahlmöglichkeiten` });
            }
        });
    });
});
function asText(v) {
    if (Array.isArray(v))
        return v.join(',');
    if (v === null || v === undefined || v === false)
        return '';
    return String(v);
}
function isEmpty(v) {
    if (Array.isArray(v))
        return v.length === 0;
    return v === undefined || v === null || v === '' || v === false;
}
export function evaluateCondition(c, data) {
    const v = data[c.field];
    const expected = c.value ?? '';
    switch (c.op) {
        case 'equals':
            return Array.isArray(v) ? v.length === 1 && v[0] === expected : asText(v) === expected;
        case 'notEquals':
            return Array.isArray(v) ? !(v.length === 1 && v[0] === expected) : asText(v) !== expected;
        case 'contains':
            return Array.isArray(v) ? v.includes(expected) : asText(v).toLowerCase().includes(expected.toLowerCase());
        case 'notContains':
            return Array.isArray(v) ? !v.includes(expected) : !asText(v).toLowerCase().includes(expected.toLowerCase());
        case 'isEmpty':
            return isEmpty(v);
        case 'notEmpty':
            return !isEmpty(v);
    }
}
export function evaluateGroup(g, data) {
    if (!g)
        return true;
    return g.mode === 'any' ? g.conditions.some((c) => evaluateCondition(c, data)) : g.conditions.every((c) => evaluateCondition(c, data));
}
/** Nächste Seite nach `pageIndex` (Index) bzw. -1 = Absenden. */
export function nextPageIndex(def, pageIndex, data) {
    const page = def.pages[pageIndex];
    const branch = page.branches.find((b) => evaluateGroup(b.when, data));
    const target = branch?.goTo ?? page.defaultNext;
    if (target === SUBMIT)
        return -1;
    if (target)
        return def.pages.findIndex((p) => p.id === target);
    return pageIndex + 1 < def.pages.length ? pageIndex + 1 : -1;
}
/** Seitenfolge, die mit diesen Antworten durchlaufen wird. */
export function pagePath(def, data) {
    const path = [];
    let i = 0;
    while (i !== -1 && !path.includes(i) && path.length <= def.pages.length) {
        path.push(i);
        i = nextPageIndex(def, i, data);
    }
    return path;
}
export function isFieldVisible(field, data) {
    return evaluateGroup(field.visibleIf, data);
}
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Prüft ein einzelnes Feld; liefert eine Fehlermeldung oder null. */
export function validateField(f, v) {
    if (STATIC_FIELD_TYPES.includes(f.type))
        return null;
    if (isEmpty(v))
        return f.required ? (f.type === 'checkbox' ? 'Bitte bestätigen' : 'Pflichtfeld') : null;
    const val = f.validation ?? {};
    switch (f.type) {
        case 'text':
        case 'textarea':
        case 'phone': {
            if (typeof v !== 'string')
                return 'Ungültiger Wert';
            const max = val.maxLength ?? (f.type === 'textarea' ? 10_000 : 500);
            if (v.length > max)
                return `Höchstens ${max} Zeichen`;
            if (val.minLength && v.trim().length < val.minLength)
                return `Mindestens ${val.minLength} Zeichen`;
            if (f.type === 'phone' && !/^[+()\d\s/-]{3,40}$/.test(v))
                return 'Ungültige Telefonnummer';
            if (val.pattern) {
                try {
                    if (!new RegExp(`^(?:${val.pattern})$`).test(v))
                        return val.patternMessage ?? 'Ungültiges Format';
                }
                catch {
                    return null;
                }
            }
            return null;
        }
        case 'email':
            return typeof v === 'string' && v.length <= 254 && EMAIL_RE.test(v) ? null : 'Ungültige E-Mail-Adresse';
        case 'number': {
            const n = typeof v === 'number' ? v : Number(v);
            if (!Number.isFinite(n))
                return 'Bitte eine Zahl eingeben';
            if (val.min !== undefined && n < val.min)
                return `Mindestens ${val.min}`;
            if (val.max !== undefined && n > val.max)
                return `Höchstens ${val.max}`;
            return null;
        }
        case 'date':
            return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v)) ? null : 'Ungültiges Datum';
        case 'select':
        case 'radio':
            return typeof v === 'string' && f.options?.some((o) => o.value === v) ? null : 'Ungültige Auswahl';
        case 'checkboxes':
            return Array.isArray(v) && v.every((x) => f.options?.some((o) => o.value === x)) ? null : 'Ungültige Auswahl';
        case 'checkbox':
            return typeof v === 'boolean' ? null : 'Ungültiger Wert';
        case 'file': {
            if (!Array.isArray(v) || v.some((x) => typeof x !== 'string'))
                return 'Ungültige Datei';
            const max = val.maxFiles ?? 5;
            return v.length > max ? `Höchstens ${max} Dateien` : null;
        }
        default:
            return null;
    }
}
/** Vollständige Prüfung einer Einsendung (Server) bzw. aller Seiten des Pfads (Client). */
export function validateSubmission(def, data) {
    const path = pagePath(def, data);
    const errors = {};
    const cleaned = {};
    for (const i of path) {
        for (const f of def.pages[i].fields) {
            if (STATIC_FIELD_TYPES.includes(f.type) || !isFieldVisible(f, data))
                continue;
            const v = data[f.key];
            const err = validateField(f, v);
            if (err)
                errors[f.key] = err;
            else if (!isEmpty(v))
                cleaned[f.key] = typeof v === 'string' ? v.trim() : v;
        }
    }
    return { errors, cleaned, path };
}
/** Prüft nur die Felder einer Seite (für „Weiter“ im Browser). */
export function validatePage(def, pageIndex, data) {
    const errors = {};
    for (const f of def.pages[pageIndex].fields) {
        if (!isFieldVisible(f, data))
            continue;
        const err = validateField(f, data[f.key]);
        if (err)
            errors[f.key] = err;
    }
    return errors;
}
/** Lesbarer Anzeigewert einer Antwort. */
export function displayValue(f, v) {
    if (isEmpty(v))
        return '';
    if (f.type === 'checkbox')
        return v ? 'Ja' : 'Nein';
    if (f.type === 'select' || f.type === 'radio')
        return f.options?.find((o) => o.value === v)?.label ?? String(v);
    if (f.type === 'checkboxes' && Array.isArray(v))
        return v.map((x) => f.options?.find((o) => o.value === x)?.label ?? x).join(', ');
    if (f.type === 'date' && typeof v === 'string') {
        const [y, m, d] = v.split('-');
        return `${d}.${m}.${y}`;
    }
    if (f.type === 'file' && Array.isArray(v))
        return `${v.length} Datei(en)`;
    return String(v);
}
/** Alle Eingabefelder der Definition (ohne Gestaltungselemente). */
export function inputFields(def) {
    return def.pages.flatMap((p) => p.fields).filter((f) => !STATIC_FIELD_TYPES.includes(f.type));
}
//# sourceMappingURL=forms.js.map